<script setup lang="ts">
import AgreementSearchTable from '@/modules/agreements/components/AgreementSearchTable.vue';
import { AgreementInfo } from '@/modules/agreements/models';
import { AgreementStatus, Side } from '@/connect';
import { Security } from '@/modules/common/models';
import { shallowRef } from 'vue';

withDefaults(
  defineProps<{
    value: AgreementInfo[];
    statuses?: AgreementStatus[];
    side?: Side;
    security?: Security | null;
    companyIdFilter?: string;
    errorMessages?: string | string[];
    rules?: Array<(value: AgreementInfo | null) => true | string>;
    useDefaultAgreements?: boolean;
    showCounterpartyBreakdown?: boolean;
  }>(),
  {
    statuses: () => [AgreementStatus.ACTIVE],
    side: undefined,
    security: undefined,
    companyIdFilter: undefined,
    errorMessages: undefined,
    rules: () => [],
    useDefaultAgreements: false,
    showCounterpartyBreakdown: false,
  }
);

const emit = defineEmits<{
  (e: 'input', value: AgreementInfo[]): void;
  (e: 'agreement-ids-eligible', agreementIds: Set<string>): void;
}>();

const isReady = shallowRef(false);
</script>

<template>
  <div>
    <VExpansionPanels accordion flat>
      <VExpansionPanel data-test-id="agreements-expansion-panel">
        <VExpansionPanelHeader expand-icon="mdi-menu-down">
          <div>
            <span style="color: green; font-weight: bold">{{ value.length }}</span>
            agreements selected
          </div>
        </VExpansionPanelHeader>
        <VExpansionPanelContent eager>
          <AgreementSearchTable
            :company-id-filter="companyIdFilter"
            :security="security"
            :show-counterparty-breakdown="showCounterpartyBreakdown"
            :side="side"
            :statuses="statuses"
            :use-default-agreements="useDefaultAgreements"
            :value="value"
            @agreement-ids-eligible="emit('agreement-ids-eligible', $event)"
            @input="emit('input', $event)"
            @ready="isReady = $event"
          />
        </VExpansionPanelContent>
      </VExpansionPanel>
    </VExpansionPanels>
    <div
      v-if="
        isReady &&
        errorMessages &&
        (Array.isArray(errorMessages) ? errorMessages.length : errorMessages)
      "
      class="px-1 mt-4"
    >
      <div class="v-alert v-alert--dense text--primary text-body-2 text-center error">
        {{ Array.isArray(errorMessages) ? errorMessages.join('\n') : errorMessages }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
::v-deep .v-expansion-panel-header {
  padding: 0;
  min-height: 56px;
}
</style>
